import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Save } from 'lucide-react'
import { usePortfolioStore } from '../store/portfolioStore'
import Button from './ui/Button'

interface CustomPortfolioModalProps {
  isOpen: boolean
  onClose: () => void
}

const PRESET_ICONS = ['📊', '💼', '🏦', '💰', '📈', '🇺🇸', '🇹🇭','🇨🇳','🇻🇳','🇺🇳', '🌟', '🚀', '💎', '🏅', '⚡', '🔥', '🎯', '💡']

// Helper function to detect portfolio type and suggest default currency
const detectPortfolioCurrency = (name: string): 'USD' | 'THB' => {
  const nameToCheck = name.toLowerCase()
  
  // Thai portfolio indicators
  const thaiKeywords = [
    'หุ้นไทย', 'ไทย', 'thai', 'thailand', 'set', 'mai',
    'บจ.', 'บริษัท', 'ประเทศไทย', 'เซ็ต', 'หุ้นตัวใหญ่',
    'lh fund', 'บีย์', 'ธนาคารไทย', 'ptt', 'cpf', 'cp',
    'กอง', 'fund', 'บจก', 'จุฬา', 'กบน', 'กฟผ', 'บขส',
    'ทองคำ', 'ทอง', 'gold', 'precious'
  ]
  
  // US portfolio indicators  
  const usKeywords = [
    'หุ้นสหรัฐ', 'สหรัฐ', 'us', 'usa', 'america', 'american',
    'nasdaq', 'nyse', 'sp500', 's&p', 'dow', 'tech', 'faang',
    'apple', 'google', 'microsoft', 'amazon', 'tesla', 'nvidia',
    'aapl', 'googl', 'msft', 'amzn', 'tsla', 'nvda',
    'wallstreet', 'silicon valley'
  ]
  
  // Check for Thai indicators first
  if (thaiKeywords.some(keyword => nameToCheck.includes(keyword))) {
    return 'THB'
  }
  
  // Check for US indicators
  if (usKeywords.some(keyword => nameToCheck.includes(keyword))) {
    return 'USD'
  }
  
  // Default to THB for ambiguous cases
  return 'THB'
}

const CustomPortfolioModal: React.FC<CustomPortfolioModalProps> = ({ isOpen, onClose }) => {
  const { 
    selectedPortfolio, 
    createCustomPortfolio, 
    updateCustomPortfolio 
  } = usePortfolioStore()

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '📊',
    color: '#3B82F6',
    currency: 'THB' as 'USD' | 'THB',
    portfolioType: '' as 'หุ้น' | 'กองทุน' | 'ทองคำ' | ''
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showIconPopover, setShowIconPopover] = useState(false)
  const iconPopoverRef = useRef<HTMLDivElement>(null)

  const isEditing = !!selectedPortfolio
  const isDefaultPortfolio = selectedPortfolio?.is_default || false

  useEffect(() => {
    if (selectedPortfolio) {
      // Map port_type to portfolioType with proper type checking
      const getPortfolioType = (portType: string | undefined): 'หุ้น' | 'กองทุน' | 'ทองคำ' | '' => {
        switch (portType) {
          case 'หุ้น':
          case 'port_stock':
            return 'หุ้น'
          case 'กองทุน':
          case 'port_fund':
            return 'กองทุน'
          case 'ทองคำ':
          case 'port_gold':
            return 'ทองคำ'
          default:
            return ''
        }
      }

      setFormData({
        name: selectedPortfolio.name,
        description: selectedPortfolio.description || '',
        icon: selectedPortfolio.icon,
        color: selectedPortfolio.color,
        currency: selectedPortfolio.currency || 'THB',
        portfolioType: getPortfolioType(selectedPortfolio.port_type)
      })
    } else {
      setFormData({
        name: '',
        description: '',
        icon: '📊',
        color: '#3B82F6',
        currency: 'THB',
        portfolioType: ''
      })
    }
    setErrors({})
  }, [selectedPortfolio, isOpen])

  // Auto-detect currency based on portfolio name (only for new portfolios)
  useEffect(() => {
    if (!isEditing && formData.name.trim()) {
      const suggestedCurrency = detectPortfolioCurrency(formData.name)
      if (suggestedCurrency !== formData.currency) {
        setFormData(prev => ({ ...prev, currency: suggestedCurrency }))
      }
    }
  }, [formData.name, isEditing])

  // Handle clicks outside icon popover
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (iconPopoverRef.current && !iconPopoverRef.current.contains(event.target as Node)) {
        setShowIconPopover(false)
      }
    }

    if (showIconPopover) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showIconPopover])

  const validateForm = () => {
    const newErrors: Record<string, string> = {}
    
    if (!isDefaultPortfolio && !formData.portfolioType) {
      newErrors.portfolioType = 'Portfolio type is required'
    }
    
    if (!formData.name.trim()) {
      newErrors.name = 'Portfolio name is required'
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Portfolio name must be at least 2 characters'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return

    setIsSubmitting(true)
    try {
      // Transform formData to match server expectations
      const getPortType = (portfolioType: string): string => {
        switch (portfolioType) {
          case 'หุ้น':
            return 'port_stock'
          case 'กองทุน':
            return 'port_fund'
          case 'ทองคำ':
            return 'port_gold'
          default:
            return portfolioType // Keep original value for backward compatibility
        }
      }

      const portfolioData = {
        name: formData.name,
        description: formData.description,
        icon: formData.icon,
        color: formData.color,
        currency: formData.currency,
        port_type: getPortType(formData.portfolioType)
      }

      if (isEditing && selectedPortfolio) {
        await updateCustomPortfolio(selectedPortfolio.id, portfolioData)
      } else {
        await createCustomPortfolio(portfolioData)
      }
      onClose()
    } catch (error) {
      console.error('Error saving portfolio:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      onClose()
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50"
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="sheet fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={handleClose}
          >
            <div
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl mx-auto border border-gray-200 dark:border-gray-700"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-lg"
                    style={{ backgroundColor: formData.color }}
                  >
                    {formData.icon}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      {isDefaultPortfolio ? 'Edit Default Portfolio' : isEditing ? 'Edit Portfolio' : 'Create Portfolio'}
                    </h2>
                    {/* <p className="text-sm text-gray-500 dark:text-gray-400">
                      {isDefaultPortfolio 
                        ? 'Update currency settings for default portfolio' 
                        : isEditing 
                        ? 'Update portfolio details' 
                        : 'Create a new custom portfolio'
                      } */}
                    {/* </p> */}
                  </div>
                </div>
                <Button variant="ghost" size="icon"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* Portfolio Type Selection */}
                {!isDefaultPortfolio && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Portfolio Type
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {([
                      { type: 'หุ้น', icon: '📈', label: 'Stock', color: '#3B82F6', active: 'border-blue-500 bg-blue-50 dark:bg-blue-900/20', dot: 'bg-blue-500' },
                      { type: 'กองทุน', icon: '🏦', label: 'Fund', color: '#10B981', active: 'border-green-500 bg-green-50 dark:bg-green-900/20', dot: 'bg-green-500' },
                      { type: 'ทองคำ', icon: '🥇', label: 'Gold', color: '#F59E0B', active: 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20', dot: 'bg-yellow-500' },
                    ] as const).map(({ type, icon, label, color, active, dot }) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, portfolioType: type, icon, color, currency: 'THB' }))}
                        className={`flex items-center gap-2 py-2.5 px-3 rounded-lg border-2 transition-all ${
                          formData.portfolioType === type ? active : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                        }`}
                      >
                        <span className="text-xl shrink-0">{icon}</span>
                        <div className="text-left min-w-0">
                          <div className="text-xs font-medium text-gray-900 dark:text-white">{type}</div>
                          <div className="text-[10px] text-gray-400 truncate">{label}</div>
                        </div>
                        {formData.portfolioType === type && <div className={`w-1.5 h-1.5 rounded-full shrink-0 ml-auto ${dot}`} />}
                      </button>
                    ))}
                  </div>
                  {errors.portfolioType && (
                    <p className="mt-1 text-sm text-loss">{errors.portfolioType}</p>
                  )}
                </div>
                )}

                {/* Portfolio Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Portfolio Name *
                  </label>
                  
                  {isDefaultPortfolio ? (
                    <div className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 flex items-center gap-2">
                      <span className="text-lg">{formData.icon}</span>
                      <span className="font-medium">{formData.name}</span>
                      <span className="text-xs bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 px-2 py-1 rounded">Default</span>
                    </div>
                  ) : (
                    <div className="relative" ref={iconPopoverRef}>
                      {/* Icon selector button inside input */}
                      <button
                        type="button"
                        onClick={() => setShowIconPopover(!showIconPopover)}
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors z-10"
                        disabled={isSubmitting}
                      >
                        <span className="text-xl">{formData.icon}</span>
                      </button>

                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder={formData.portfolioType ? `` : "กรุณาเลือกประเภทพอร์ท"}
                        className={`w-full pl-14 pr-20 py-3 rounded-lg border-2 transition-colors ${
                          errors.name
                            ? 'border-red-300 focus:border-primary-500'
                            : 'border-gray-200 dark:border-gray-600 focus:border-primary-500'
                        } bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${!formData.portfolioType ? 'bg-gray-50 dark:bg-gray-800' : ''}`}
                        disabled={isSubmitting || !formData.portfolioType}
                      />
                      
                      {formData.portfolioType && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                          <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-600 rounded text-gray-600 dark:text-gray-300">
                            {formData.portfolioType}
                          </span>
                        </div>
                      )}

                      {/* Icon Popover */}
                      <AnimatePresence>
                        {showIconPopover && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: -10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: -10 }}
                            className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-50 p-4"
                          >
                            <div className="grid grid-cols-8 gap-3 max-h-48 overflow-y-auto">
                              {PRESET_ICONS.map((icon) => (
                                <button
                                  key={icon}
                                  type="button"
                                  onClick={() => {
                                    setFormData({ ...formData, icon })
                                    setShowIconPopover(false)
                                  }}
                                  className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg transition-all ${
                                    formData.icon === icon
                                      ? 'bg-blue-100 dark:bg-blue-900 ring-2 ring-blue-500'
                                      : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                                  }`}
                                  disabled={isSubmitting}
                                >
                                  {icon}
                                </button>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}
                  
                  {/* Auto-detection indicator */}
                  {!isEditing && formData.name.trim() && (
                    <div className="mt-2 flex items-center gap-2">
                      <div className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1">
                        <span>💡</span>
                        <span>Auto-detected currency: {detectPortfolioCurrency(formData.name) === 'USD' ? '🇺🇸 USD' : '🇹🇭 THB'}</span>
                      </div>
                    </div>
                  )}
                  
                  {errors.name && (
                    <p className="mt-1 text-sm text-loss">{errors.name}</p>
                  )}
                </div>

                {/* Description */}
                {!isDefaultPortfolio && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Optional description for this portfolio..."
                    rows={3}
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 dark:border-gray-600 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                    disabled={isSubmitting}
                  />
                </div>
                )}

                {/* Currency Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Portfolio Currency *
                  </label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, currency: 'THB' })}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors ${
                        formData.currency === 'THB'
                          ? 'border-transparent bg-brand'
                          : 'border-transparent bg-gradient-to-r from-blue-100 to-purple-100 hover:from-blue-200 hover:to-purple-200 text-primary-700 dark:from-blue-900/50 dark:to-purple-900/50 dark:text-blue-200'
                      }`}
                      disabled={isSubmitting}
                    >
                      <span className="text-lg">🇹🇭</span>
                      <span className="font-medium">Thai Baht (THB)</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, currency: 'USD' })}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors ${
                        formData.currency === 'USD'
                          ? 'border-transparent bg-brand'
                          : 'border-transparent bg-gradient-to-r from-blue-100 to-purple-100 hover:from-blue-200 hover:to-purple-200 text-primary-700 dark:from-blue-900/50 dark:to-purple-900/50 dark:text-blue-200'
                      }`}
                      disabled={isSubmitting}
                    >
                      <span className="text-lg">🇺🇸</span>
                      <span className="font-medium">US Dollar (USD)</span>
                    </button>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-6">
                  <Button className="flex-1"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    <Save className="w-4 h-4" />
                    {isSubmitting ? 'Saving...' : isDefaultPortfolio ? 'Update Currency' : isEditing ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export default CustomPortfolioModal